import React, {memo, useState} from "react";
import {endOfDay, formatISO, parseISO, startOfDay} from "date-fns";

import {isValidDate} from "../../../../utils/date";
import FiltersBox from "../../../../components/FiltersBox";
import SeasonSelect from "../../../../components/inputs/SeasonSelect";
import DateGte from "../../../../components/inputs/DateGte";
import DateLte from "../../../../components/inputs/DateLte";

import {Grid} from "@mui/material";
import Stack from "@mui/material/Stack";
import SelectFromRegistry from "../../../../components/inputs/SelectFromRegistry";
import {useTranslation} from "react-i18next";
import RegionAutocomplete from "../../../../components/inputs/RegionAutocomplete";
import ProvinceAutocomplete from "../../../../components/inputs/ProvinceAutocomplete";
import MunicipalityAutocomplete from "../../../../components/inputs/MunicipalityAutocomplete";


const Filters = memo(({onChange, initialValues, loading, incidentRegistry, incidentSettings}) => {
    const {t} = useTranslation("incidents");
    const incidentCategoryRegistry = incidentRegistry?.incidentCategoryV2 ?? [];
    const incidentSubcategoryRegistry = incidentRegistry?.incidentSubcategory ?? [];

    const [dateGte, setDateGte] = useState(parseISO(initialValues.dateGte ?? null));
    const [dateLte, setDateLte] = useState(parseISO(initialValues.dateLte ?? null));
    const [incidentCategory, setIncidentCategory] = useState(initialValues.incidentCategory ?? null);
    const [incidentSubcategory, setIncidentSubcategory] = useState(initialValues.incidentSubcategory ?? null);
    const [regionId, setRegionId] = useState(initialValues.regionId ?? null);
    const [provinceId, setProvinceId] = useState(initialValues.provinceId ?? null);
    const [municipalityId, setMunicipalityId] = useState(initialValues.municipalityId ?? null);

    const filters = {
        "date__gte": isValidDate(dateGte) ? formatISO(startOfDay(dateGte)) : null,
        "date__lte": isValidDate(dateLte) ? formatISO(endOfDay(dateLte)) : null,
        "incident_category": incidentCategory,
        "incident_subcategory": incidentSubcategory,
        "region_id": regionId,
        "province_id": provinceId,
        "municipality_id": municipalityId,
    }

    const filteredSubcategoryRegistry = incidentSubcategoryRegistry.filter((subcategory) => (
        incidentSettings.incidentCategoryGroups?.[incidentCategory]?.includes(subcategory.code)
    ));

    const handleIncidentCategoryChange = (event) => {
        const value = event.target.value;
        setIncidentCategory(value);
        setIncidentSubcategory(null);
    }

    const handleIncidentSubcategoryChange = (event) => {
        const value = event.target.value;
        setIncidentSubcategory(value);
    }

    const handleOnApplyFilters = () => {
        onChange(filters);
    }

    const handleRegionChange = (regionId) => {
        setRegionId(regionId);
        setProvinceId(null);
        setMunicipalityId(null);
    }

    const handleProvinceChange = (provinceId) => {
        setProvinceId(provinceId);
        setMunicipalityId(null);
    }

    return <FiltersBox onApplyFilters={handleOnApplyFilters} isLoading={loading}>
        <Grid container spacing={2} justifyContent="space-between" alignItems="center">
            <Grid item xs={12} lg={4}>
                    <SeasonSelect dateGte={dateGte} dateLte={dateLte} setDateGte={setDateGte}
                                  fullWidth
                                  setDateLte={setDateLte} sx={{minWidth: "7rem"}}/>
            </Grid>
            <Grid item xs={12} lg={4} alignItems="start">
                <Stack direction="row" spacing={2}>
                    <DateGte dateGte={dateGte} setDateGte={setDateGte} maxDate={dateLte}/>
                    <DateLte dateLte={dateLte} setDateLte={setDateLte} mindate={dateGte}/>
                </Stack>
            </Grid>
            <Grid item xs={12} lg={4} alignItems="start">
                <Stack direction="row" spacing={2}>
                    <SelectFromRegistry value={incidentCategory} onChange={handleIncidentCategoryChange}
                                        label={t('filters.incidentCategory')} registry={incidentCategoryRegistry}/>
                    <SelectFromRegistry value={incidentSubcategory} onChange={handleIncidentSubcategoryChange}
                                        label={t('filters.incidentSubcategory')} registry={filteredSubcategoryRegistry}
                                        disabled={filteredSubcategoryRegistry.length === 0}/>
                </Stack>
            </Grid>
            <Grid item xs={12} lg={6}>
                <Stack direction="row" spacing={2}>
                    <RegionAutocomplete name="regionId"
                                        value={regionId}
                                        onChange={handleRegionChange}
                                        MDInputProps={{
                                            name: 'regionId',
                                            label: t('filters.regionId'),
                                        }}
                    />
                    <ProvinceAutocomplete name="provinceId"
                                          regionId={regionId}
                                          value={provinceId}
                                          onChange={handleProvinceChange}
                                          MDInputProps={{
                                              name: 'provinceId',
                                              label: t('filters.provinceId'),
                                          }}/>
                    <MunicipalityAutocomplete name="municipalityId"
                                              provinceId={provinceId}
                                              regionId={regionId}
                                              value={municipalityId}
                                              onChange={setMunicipalityId}
                                              MDInputProps={{
                                                  name: 'municipalityId',
                                                  label: t('filters.municipalityId'),
                                              }}/>
                </Stack>
            </Grid>
        </Grid>
    </FiltersBox>
})

export default Filters;